import { Link, useLocation } from "react-router-dom";
import { format, isSameDay } from "date-fns";
import type { PublicEvent } from "@vedaunion/shared";
import { modalLinkState } from "../../lib/modal-routing";

/**
 * Editorial list rendering for events. Used for "upcoming" strips on the
 * landing page and the dashboard. Groups by day, day labelled in display font,
 * each event a single-line item with category eyebrow + time + title.
 */

interface Props {
  events: PublicEvent[];
  basePath?: string;
  emptyState?: React.ReactNode;
  /** Cap rendered count (after grouping). */
  limit?: number;
}

export function EventList({ events, basePath = "/calendar", emptyState, limit }: Props) {
  const location = useLocation();
  if (events.length === 0) {
    return (
      <p className="font-display-italic text-[1.05rem] text-[var(--color-ink-mute)]">
        {emptyState ?? "No events scheduled yet."}
      </p>
    );
  }

  const trimmed = typeof limit === "number" ? events.slice(0, limit) : events;

  // Group consecutive events by day
  const groups: { day: Date; items: PublicEvent[] }[] = [];
  for (const ev of trimmed) {
    const day = new Date(ev.startsAt);
    const last = groups[groups.length - 1];
    if (last && isSameDay(last.day, day)) {
      last.items.push(ev);
    } else {
      groups.push({ day, items: [ev] });
    }
  }

  return (
    <ul className="flex flex-col gap-7">
      {groups.map((g, idx) => (
        <li key={idx} className="grid grid-cols-[88px_1fr] gap-4 sm:grid-cols-[120px_1fr] sm:gap-6">
          <div className="text-right">
            <p className="font-display-italic text-[var(--color-violet)] text-[1.15rem] leading-[1] mb-1">
              {format(g.day, "MMM").toLowerCase()}
            </p>
            <p className="font-display text-[clamp(1.85rem,4vw,2.25rem)] leading-[0.9] tracking-tight">
              {format(g.day, "dd")}
            </p>
            <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] mt-1">
              {format(g.day, "EEE")}
            </p>
          </div>
          <div className="border-l border-[var(--color-rule)] pl-4 sm:pl-6 flex flex-col gap-4">
            {g.items.map((ev) => (
              <Link
                key={ev.id}
                to={`${basePath}/${ev.slug}`}
                state={modalLinkState(location)}
                className="group block hover:opacity-90 transition-opacity"
              >
                <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-violet)] mb-1.5">
                  {ev.category.replace(/-/g, " ")}
                  <span className="mx-2 text-[var(--color-ink-mute)]">·</span>
                  <span className="text-[var(--color-ink-mute)]">
                    {ev.allDay ? "All day" : ev.timeTbd ? "Time TBA" : format(new Date(ev.startsAt), "HH:mm")}
                  </span>
                  {ev.locationType !== "in-person" && (
                    <>
                      <span className="mx-2 text-[var(--color-ink-mute)]">·</span>
                      <span className="text-[var(--color-ink-mute)]">{ev.locationType}</span>
                    </>
                  )}
                </p>
                <h3 className="font-display text-[1.4rem] sm:text-[1.55rem] leading-[1.15] tracking-[-0.01em] group-hover:text-[var(--color-violet)] transition-colors">
                  {ev.title}
                </h3>
                {ev.summary && (
                  <p className="text-[0.97rem] leading-[1.55] text-[var(--color-ink-soft)] mt-1.5 max-w-[60ch]">
                    {ev.summary}
                  </p>
                )}
                {ev.location && (
                  <p className="font-grotesk text-[0.7rem] tracking-[0.12em] uppercase text-[var(--color-ink-mute)] mt-2">
                    {ev.location}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </li>
      ))}
    </ul>
  );
}
